/**
 * Algolia Native Filters - Sidebar Drawer Enhancement
 *
 * Enhances the Algolia native InstantSearch DOM to add:
 * - Desktop: Sticky sidebar with viewport-bounded scrollbar
 * - Mobile: Full-screen drawer with overlay, close button, and apply button
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 768;

  let isDrawerOpen = false;
  let overlay = null;
  let drawerHeader = null;
  let drawerFooter = null;
  let facetsPlaceholder = null; // Placeholder to remember original DOM position

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /**
   * Wait for the Algolia facets to be ready, then initialize.
   */
  function waitForFacets() {
    const facets = document.querySelector('.ais-facets.is-ready');
    if (facets) {
      init(facets);
      return;
    }

    const observer = new MutationObserver(function (mutations, obs) {
      const readyFacets = document.querySelector('.ais-facets.is-ready');
      if (readyFacets) {
        obs.disconnect();
        init(readyFacets);
      }
    });

    const target = document.getElementById('algolia-shopify-instantsearch') || document.body;
    observer.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Main initialization — restructure DOM and add drawer behavior.
   */
  function init(facets) {
    const aisPage = facets.closest('.ais-page');
    if (!aisPage) return;

    restructureLayout(aisPage, facets);
    injectDrawerElements(facets);
    bindEvents(facets, aisPage);
    aisPage.classList.add('ais-layout-ready');
  }

  /**
   * Restructure .ais-page to a proper sidebar + main content layout.
   */
  function restructureLayout(aisPage, facets) {
    // Wrap .ais-block and .ais-pagination-container in a main content wrapper
    const block = aisPage.querySelector('.ais-block');
    const pagination = aisPage.querySelector('.ais-pagination-container');
    if (!block) return;

    const mainContent = document.createElement('div');
    mainContent.className = 'ais-main-content';

    // Move facets-button into the search header for mobile positioning
    const facetsButton = aisPage.querySelector('.ais-facets-button');
    const searchHeader = block.querySelector('.ais-search-header');
    if (facetsButton && searchHeader) {
      searchHeader.insertBefore(facetsButton, searchHeader.firstChild);
    }

    // Insert main content wrapper
    block.parentNode.insertBefore(mainContent, block);
    mainContent.appendChild(block);
    if (pagination) {
      mainContent.appendChild(pagination);
    }

    // Relocate server-rendered breadcrumbs into the Algolia layout
    var breadcrumbs = document.getElementById('algolia-breadcrumbs');
    if (breadcrumbs) {
      mainContent.insertBefore(breadcrumbs, mainContent.firstChild);
      breadcrumbs.style.display = '';

      // Move search header (sort + view toggle) into the breadcrumbs row
      if (searchHeader) {
        breadcrumbs.appendChild(searchHeader);
      }

      // Results count is updated via the onRender listener registered at script load
    }
  }

  /**
   * Inject mobile drawer elements: overlay, header, footer.
   */
  function injectDrawerElements(facets) {
    // Overlay
    overlay = document.createElement('div');
    overlay.className = 'ais-filters-overlay';
    facets.parentNode.insertBefore(overlay, facets);

    // Drawer header
    drawerHeader = document.createElement('div');
    drawerHeader.className = 'ais-filters-drawer-header';
    drawerHeader.innerHTML =
      '<h2 class="ais-filters-drawer-title">All Filters</h2>' +
      '<button class="ais-filters-drawer-close" aria-label="Close filters">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">' +
          '<path stroke="#6d6d6d" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>' +
        '</svg>' +
      '</button>';
    facets.insertBefore(drawerHeader, facets.firstChild);

    // Drawer footer
    drawerFooter = document.createElement('div');
    drawerFooter.className = 'ais-filters-drawer-footer';
    drawerFooter.innerHTML = '<button class="ais-filters-drawer-apply">SHOW ALL RESULTS</button>';
    facets.appendChild(drawerFooter);
  }

  /**
   * Bind all events for drawer open/close and resize.
   */
  function bindEvents(facets, aisPage) {
    // Open drawer via the facets button
    const facetsButton = aisPage.querySelector('.ais-facets-button');
    if (facetsButton) {
      facetsButton.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openDrawer(facets);
      });
    }

    // Close drawer events
    var closeBtn = drawerHeader.querySelector('.ais-filters-drawer-close');
    if (closeBtn) closeBtn.addEventListener('click', function () { closeDrawer(facets); });

    if (overlay) overlay.addEventListener('click', function () { closeDrawer(facets); });

    var applyBtn = drawerFooter.querySelector('.ais-filters-drawer-apply');
    if (applyBtn) applyBtn.addEventListener('click', function () { closeDrawer(facets); });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isDrawerOpen) {
        closeDrawer(facets);
      }
    });

    // On resize, close drawer if switching to desktop
    window.addEventListener('resize', function () {
      if (!isMobile() && isDrawerOpen) {
        closeDrawer(facets);
      }
    });
  }

  function openDrawer(facets) {
    isDrawerOpen = true;

    // Move facets + overlay to document.body to escape any parent with
    // CSS transform (which breaks position:fixed containing block).
    if (!facetsPlaceholder) {
      facetsPlaceholder = document.createComment('ais-facets-placeholder');
    }
    facets.parentNode.insertBefore(facetsPlaceholder, facets);
    document.body.appendChild(overlay);
    document.body.appendChild(facets);

    // Force reflow so the browser paints the initial state (translateX(-100%))
    // before the transition to translateX(0) begins
    void facets.offsetHeight;

    facets.classList.add('ais-filters-open');
    overlay.classList.add('ais-filters-overlay-visible');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer(facets) {
    isDrawerOpen = false;
    facets.classList.remove('ais-filters-open');
    overlay.classList.remove('ais-filters-overlay-visible');
    document.body.style.overflow = '';

    // Wait for the slide-out transition to finish before moving elements back
    setTimeout(function () {
      if (!isDrawerOpen && facetsPlaceholder && facetsPlaceholder.parentNode) {
        facetsPlaceholder.parentNode.insertBefore(overlay, facetsPlaceholder);
        facetsPlaceholder.parentNode.insertBefore(facets, facetsPlaceholder);
        facetsPlaceholder.parentNode.removeChild(facetsPlaceholder);
      }
    }, 300);
  }

  // Update results count on every Algolia render.
  // Skip the first render when URL has facet params — the first results are
  // unfiltered and would flash the wrong count (e.g. "6082 Results" → "465 Results").
  var _filtersRenderCount = 0;
  document.addEventListener('algolia.instantSearch.onRender', function () {
    _filtersRenderCount++;
    var el = document.querySelector('.algolia-results-count');
    if (!el) return;
    // On first render with facet params, don't update the count — wait for filtered results
    if (_filtersRenderCount === 1) {
      var qs = new URLSearchParams(window.location.search);
      var SKIP = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','fbclid','gclid','ref','preview_theme_id'];
      var hasFacets = Array.from(qs.keys()).some(function(k) { return SKIP.indexOf(k) === -1 && k.indexOf('utm_') !== 0; });
      if (hasFacets) return;
    }
    var lastResults = window.algoliaShopify &&
      window.algoliaShopify.instantsearch &&
      window.algoliaShopify.instantsearch.search &&
      window.algoliaShopify.instantsearch.search.helper &&
      window.algoliaShopify.instantsearch.search.helper.lastResults;
    if (lastResults && typeof lastResults.nbHits === 'number') {
      var n = lastResults.nbHits.toLocaleString();
      el.textContent = n + ' Result' + (lastResults.nbHits === 1 ? '' : 's');
    }
  });

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForFacets);
  } else {
    waitForFacets();
  }
})();
